import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { fmt, toolLabel, toolEngineLabel, prettyModelId, superGroupOf, TOOL_LINE_COLORS } from "@/lib/format";
import { GroupIcon } from "@/lib/groupIcons";

export default function UsageTimeline({
  timeline, page, pageSize, total, onPageChange,
  sessions, sessionPage, sessionPageSize, sessionTotal, onSessionPageChange,
}) {
  const [view, setView] = useState("session"); // "session" | "flat"

  return (
    <section className="rounded-xl bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_4px_12px_-6px_rgba(15,23,42,0.08)]">
      <div className="mb-3 flex items-center justify-end">
        <div className="flex items-center gap-3">
          <div className="flex rounded-md border border-ink-200 p-0.5 text-xs">
            <ViewBtn active={view === "session"} onClick={() => setView("session")}>세션별</ViewBtn>
            <ViewBtn active={view === "flat"} onClick={() => setView("flat")}>전체 채팅</ViewBtn>
          </div>
          <span className="text-xs text-ink-500">
            {view === "session" ? `세션 ${fmt.int(sessionTotal || 0)}개` : `총 ${fmt.int(total || 0)}건`}
          </span>
        </div>
      </div>

      {view === "session" ? (
        <SessionView
          sessions={sessions || []}
          page={sessionPage}
          pageSize={sessionPageSize}
          total={sessionTotal}
          onPageChange={onSessionPageChange}
        />
      ) : (
        <FlatView
          items={timeline || []}
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={onPageChange}
        />
      )}
    </section>
  );
}

function ViewBtn({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded px-2.5 py-1 font-medium transition-colors ${
        active ? "bg-brand-600 text-white" : "text-ink-600 hover:bg-ink-100"
      }`}
    >
      {children}
    </button>
  );
}

function SessionView({ sessions, page, pageSize, total, onPageChange }) {
  const [openKey, setOpenKey] = useState(null);
  const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 20)));

  if (sessions.length === 0) {
    return <div className="text-sm text-ink-400">데이터 없음</div>;
  }

  return (
    <>
      <div className="divide-y divide-ink-100 rounded-lg border border-ink-200">
        {sessions.map((s) => {
          const open = openKey === s.sessionKey;
          return (
            <div key={s.sessionKey}>
              <button
                type="button"
                onClick={() => setOpenKey(open ? null : s.sessionKey)}
                className={`flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-ink-50 focus:outline-none focus-visible:bg-brand-50/60 ${open ? "bg-brand-50/40" : ""}`}
              >
                <ChevronDown className={`h-4 w-4 shrink-0 text-ink-400 transition-transform ${open ? "" : "-rotate-90"}`} />
                <span className="w-36 shrink-0 whitespace-nowrap font-mono text-xs text-ink-500">
                  {fmt.kstDateTime(s.lastAt).slice(0, 16)}
                </span>
                <span className="flex-1 truncate text-ink-700">
                  {s.tools.length === 0 && "-"}
                  {s.tools.map((t, ti) => (
                    <span key={`${t}-${ti}`} className="mr-2 inline-flex items-center gap-1 align-middle">
                      <GroupIcon superGroup={superGroupOf(t)} color={TOOL_LINE_COLORS[superGroupOf(t)]} className="h-3 w-3" />
                      {toolLabel(t)}
                    </span>
                  ))}
                  {!s.conversationId && (
                    <span className="ml-1.5 text-xs text-ink-400">(대화 미연결 · 단일 턴)</span>
                  )}
                </span>
                <span className="shrink-0 text-xs text-ink-500">{s.turnCount}턴</span>
                <span className="w-20 shrink-0 text-right text-sm font-medium tabular-nums text-ink-900">
                  {fmt.nc(s.ncUsed)}
                </span>
              </button>
              {open && (
                <div className="border-t border-ink-100 bg-ink-50/60 px-3 py-2">
                  <TurnsTable items={s.turns} compact />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} />
    </>
  );
}

function FlatView({ items, page, pageSize, total, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 50)));
  if (items.length === 0) {
    return <div className="text-sm text-ink-400">데이터 없음</div>;
  }
  return (
    <>
      <TurnsTable items={items} />
      <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} />
    </>
  );
}

function TurnsTable({ items, compact = false }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-ink-200 text-sm">
        <thead className={compact ? "" : "bg-ink-50"}>
          {/* 도구·엔진 조합 표기 통일(2026-07-18) — 내부 값(c7/t5 등) 대신 본체 서비스 라벨. */}
          <tr>
            <Th>시각 (KST)</Th>
            <Th>도구·엔진</Th>
            <Th>모델</Th>
            <Th align="right">입력</Th>
            <Th align="right">출력</Th>
            <Th align="right">캐시 hit</Th>
            <Th align="right">크레딧</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {items.map((r) => (
            <tr key={`${r.createdAt}-${r.conversationId}`}>
              <Td className="whitespace-nowrap font-mono text-xs">
                {fmt.kstDateTime(r.createdAt)}
              </Td>
              <Td className="whitespace-nowrap">
                {r.tool ? (
                  <span className="inline-flex items-center gap-1.5">
                    <GroupIcon superGroup={superGroupOf(r.tool)} color={TOOL_LINE_COLORS[superGroupOf(r.tool)]} className="h-3.5 w-3.5" />
                    {toolEngineLabel(r.tool, r.engine)}
                  </span>
                ) : "-"}
              </Td>
              <Td className="whitespace-nowrap text-xs text-ink-500">{prettyModelId(r.modelId)}</Td>
              <Td align="right" className="tabular-nums">{fmt.int(r.inputTokens)}</Td>
              <Td align="right" className="tabular-nums">{fmt.int(r.outputTokens)}</Td>
              <Td align="right" className="tabular-nums">{fmt.int(r.cacheReadTokens)}</Td>
              <Td align="right" className="tabular-nums font-medium">{fmt.nc(r.ncCost)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pagination({ page, totalPages, onPageChange }) {
  return (
    <div className="mt-3 flex items-center justify-end gap-2 text-xs text-ink-600">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        className="rounded border border-ink-200 px-2 py-1 disabled:opacity-40"
      >
        이전
      </button>
      <span>{page} / {totalPages}</span>
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
        className="rounded border border-ink-200 px-2 py-1 disabled:opacity-40"
      >
        다음
      </button>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th className={`px-3 py-2 text-${align} text-xs font-semibold uppercase tracking-wide text-ink-500`}>
      {children}
    </th>
  );
}

function Td({ children, align = "left", className = "" }) {
  return <td className={`px-3 py-2 text-${align} ${className}`}>{children}</td>;
}
